import { nullIfString, stringIfNull } from '@/lib/string-utils';
import { FrequentTimeZones, getLearningLossLabel } from '@airreading/common';
import {
  AdminSession,
  learningLossSelectableSchema,
  learningLossSelectableValues,
  LearningPathWithLessons,
} from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PencilIcon } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { DefaultValues, UseFormReturn } from 'react-hook-form';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';

import { FormModal, SubmitProps } from '@/components/form-modal';
import { FormComboBox } from '@/components/forms/form-combo-box';
import { FormInputDate } from '@/components/forms/form-input-date';
import { FormInputTime } from '@/components/forms/form-input-time';
import { FormMultiSelect } from '@/components/forms/form-multi-select';
import { FormSingleSelect } from '@/components/forms/form-single-select';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/hooks/use-toast';
import {
  buildNameLikeSelectOptions,
  buildUserSelectOptions,
  comboBoxOptionsOf,
  emptySelectOption,
  requiredMultiSelectOptions,
  SelectOption,
  selectOption,
} from '@/components/ui/multi-select-utils';

import { studentsTypeaheadSearch, useAllLearningPaths, userTypeaheadSearch } from '@/lib/api-call-hooks';
import { getAdminSessions, updateSessionDetails, updateSessionStudents } from '@/lib/api-calls';
import { airServerErrorFrom } from '@/lib/error-handling';

const formSchema = z.object({
  supportAdmin: selectOption,
  students: requiredMultiSelectOptions(),
  date: z.instanceof(Temporal.PlainDate),
  startTime: z.instanceof(Temporal.PlainTime),
  duration: z.enum(['30', '40']),
  learningPath: selectOption.refine(({ value }) => value !== '', { message: 'Learning path is required' }),
  lesson: selectOption.refine(({ value }) => value !== '', { message: 'Lesson is required' }),
  learningLoss: learningLossSelectableSchema,
});
type FormData = z.infer<typeof formSchema>;

export function EditSessionModal({ session, triggerTestId }: { session: AdminSession; triggerTestId?: string }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { mutateAsync: updateSessionDetailsMutate } = useMutation({ mutationFn: updateSessionDetails });
  const { mutateAsync: updateSessionStudentsMutate } = useMutation({ mutationFn: updateSessionStudents });
  const adminTypeahead = userTypeaheadSearch(queryClient, { roles: ['school_admin', 'org_admin', 'air_admin'] });
  const schoolId = session.school?.id || session.studentGroupId;
  const studentTypeahead = studentsTypeaheadSearch(queryClient, { schools: [schoolId] });
  const { learningPaths, isPending: isLoadingLearningPaths } = useAllLearningPaths();

  const dt = session.startInstant.toZonedDateTimeISO(FrequentTimeZones.UsCentral);
  const defaultDate = dt.toPlainDate();
  const defaultStartTime = Temporal.PlainTime.from({ hour: dt.hour, minute: dt.minute });

  // Calculate duration from session's start and end times
  const durationMinutes = session.endInstant.until(session.startInstant).total('minutes');
  const defaultDuration = Math.abs(durationMinutes).toString() as '30' | '40';

  const defaultValues: DefaultValues<FormData> = {
    supportAdmin: session.supportAdmin ? buildUserSelectOptions(session.supportAdmin) : emptySelectOption,
    students: session.students.map((s) => ({ label: s.fullNameOrEmail ?? '', value: s.id })),
    date: defaultDate,
    startTime: defaultStartTime,
    duration: defaultDuration,
    learningPath: buildNameLikeSelectOptions(session.learningPath),
    lesson: session.lesson ? buildNameLikeSelectOptions(session.lesson) : emptySelectOption,
    learningLoss: stringIfNull(session.learningLoss),
  };

  const learningPathOptions = useMemo(() => learningPaths.map(buildNameLikeSelectOptions), [learningPaths]);

  const onSubmit = async (submitProps: SubmitProps<FormData>) => {
    try {
      const { formData, closeModal } = submitProps;
      const plainDate = formData.date;
      const startTime = formData.startTime;
      const durationMinutes = parseInt(formData.duration);
      const endTime = startTime.add({ minutes: durationMinutes });
      const timeZone = FrequentTimeZones.UsCentral;
      const start = plainDate.toZonedDateTime({ plainTime: startTime, timeZone });
      const end = plainDate.toZonedDateTime({ plainTime: endTime, timeZone });

      // Determine whether the admin actually changed anything. We only want to notify the teacher
      // (via the session update email) when a real edit was made — saving the modal untouched should
      // not send an email. Compared against the original session values used to build `defaultValues`.
      const newStudentIds = formData.students.map((s) => s.value);
      const originalStudentIds = session.students.map((s) => s.id);
      const studentsChanged =
        newStudentIds.length !== originalStudentIds.length ||
        newStudentIds.some((id) => !originalStudentIds.includes(id));

      const hasNonRosterChanges =
        !start.toInstant().equals(session.startInstant) ||
        !end.toInstant().equals(session.endInstant) ||
        formData.lesson.value !== (session.lesson?.id ?? '') ||
        formData.supportAdmin.value !== (session.supportAdmin?.id ?? '') ||
        formData.learningLoss !== stringIfNull(session.learningLoss);

      const hasChanges = hasNonRosterChanges || studentsChanged;

      // Roster goes through the dedicated students API
      if (studentsChanged) {
        await updateSessionStudentsMutate({
          sessionId: session.id,
          payload: {
            studentIds: newStudentIds,
            // Let details own the single update-confirmation email when anything changed.
            shouldSendUpdateConfirmationEmail: false,
          },
        });
      }

      await updateSessionDetailsMutate({
        id: session.id,
        data: {
          status: session.status,
          start: start.toInstant().toString(),
          end: end.toInstant().toString(),
          lessonId: formData.lesson.value,
          learningLoss: nullIfString(formData.learningLoss),
          supportAdminId: formData.supportAdmin.value || null,
          // Only notify the teacher when something actually changed.
          shouldSendEmail: hasChanges,
        },
      });

      toast({ title: 'Session updated successfully' });
      closeModal();
      queryClient.invalidateQueries(getAdminSessions());
    } catch (error) {
      const errorMessage =
        airServerErrorFrom(error)?.userFacingMessage ??
        (error instanceof Error ? error.message : 'There was an error updating the session. Please try again.');

      toast({
        title: 'Failed to update session',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  return (
    <FormModal
      modalTitle="Edit session"
      formSchema={formSchema}
      onSubmit={onSubmit}
      confirmBtnLabel="Save"
      cancelBtnLabel="Cancel"
      trigger={
        <Button variant="ghost" size="icon" data-testid={triggerTestId}>
          <PencilIcon size={16} />
        </Button>
      }
      defaultValues={defaultValues}
      resetOnDefaultValuesChange
    >
      {({ form }) => (
        <EditSessionModalFields
          form={form}
          adminTypeahead={adminTypeahead}
          studentTypeahead={studentTypeahead}
          learningPathOptions={learningPathOptions}
          learningPaths={learningPaths}
          isLoadingLearningPaths={isLoadingLearningPaths}
        />
      )}
    </FormModal>
  );
}

function EditSessionModalFields({
  form,
  adminTypeahead,
  studentTypeahead,
  learningPathOptions,
  learningPaths,
  isLoadingLearningPaths,
}: {
  form: UseFormReturn<FormData>;
  adminTypeahead: ReturnType<typeof userTypeaheadSearch>;
  studentTypeahead: ReturnType<typeof studentsTypeaheadSearch>;
  learningPathOptions: SelectOption[];
  learningPaths: LearningPathWithLessons[];
  isLoadingLearningPaths: boolean;
}) {
  const selectedLearningPathId = form.watch('learningPath')?.value;

  const selectedLearningPath = useMemo(
    () => learningPaths.find((lp) => lp.id === selectedLearningPathId),
    [learningPaths, selectedLearningPathId],
  );

  const lessonOptions = useMemo(
    () =>
      (selectedLearningPath?.learningPathLessons || [])
        .toSorted((a, b) => a.order - b.order)
        .map(({ lesson }) => buildNameLikeSelectOptions(lesson)),
    [selectedLearningPath],
  );

  useEffect(() => {
    if (!selectedLearningPathId || !selectedLearningPath) {
      return;
    }
    const currentLessonId = form.getValues('lesson')?.value;
    if (!currentLessonId) {
      return;
    }
    const lessonIsInSelectedPath = selectedLearningPath.learningPathLessons.some(
      ({ lesson }) => lesson.id === currentLessonId,
    );
    if (!lessonIsInSelectedPath) {
      form.setValue('lesson', emptySelectOption, { shouldDirty: true, shouldValidate: true });
    }
  }, [form, selectedLearningPath, selectedLearningPathId]);

  return (
    <div className="space-y-4 mt-4">
      <FormComboBox
        form={form}
        name="supportAdmin"
        label="Support admin"
        triggerProps={{ className: 'w-full' }}
        placeholder="Select an option"
        {...comboBoxOptionsOf(adminTypeahead)}
      />
      <FormMultiSelect
        form={form}
        name="students"
        label="Students"
        multipleSelectorProps={{
          onSearch: studentTypeahead,
          className: 'w-full',
          placeholder: 'Select an option',
        }}
      />
      <div className="grid grid-cols-2 gap-4">
        <FormSingleSelect
          form={form}
          name="learningLoss"
          label="Learning loss"
          options={learningLossSelectableValues}
          renderOption={getLearningLossLabel}
          triggerProps={{ className: 'w-full' }}
        />
        <FormInputDate form={form} name="date" label="Date" triggerButtonProps={{ className: 'w-full' }} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <FormInputTime
          form={form}
          name="startTime"
          label="Start time (CT)"
          triggerButtonProps={{ className: 'w-full' }}
          step={Temporal.Duration.from({ minutes: 1 })}
        />
        <FormSingleSelect
          form={form}
          name="duration"
          label="Class duration"
          options={['30', '40']}
          renderOption={(value) => `${value} minutes`}
          triggerProps={{ className: 'w-full' }}
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <FormComboBox
          form={form}
          name="learningPath"
          label="Learning path"
          isLoadingKnownOptions={isLoadingLearningPaths}
          triggerProps={{ className: 'w-full' }}
          placeholder="Select an option"
          {...comboBoxOptionsOf(learningPathOptions)}
        />
        <FormComboBox
          form={form}
          name="lesson"
          label="Lesson"
          isLoadingKnownOptions={isLoadingLearningPaths}
          triggerProps={{
            className: 'w-full',
            disabled: !selectedLearningPathId,
          }}
          placeholder="Select an option"
          {...comboBoxOptionsOf(lessonOptions)}
        />
      </div>
    </div>
  );
}
